import { ChangeDetectionStrategy, Component } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';

/**
 * Card #601 (piece 3 of #587's plan): one line of the "What goes in each
 * column" guide under the item upload's sample CSV link.
 */
export interface ItemCsvHelpColumn {
  /** COMMON.<key>: the column's title, the same key the sample CSV header is translated from */
  labelKey: string;
  /** COMMON.ITEM_CSV_HELP.<key>: what to put in that column */
  helpKey: string;
  /** the import refuses to run without it (upload-csv.component.ts headingType) */
  required?: boolean;
}

/**
 * What each item column takes, as the item import reads it (merch-backend
 * common.controller.mjs itemsCreationProcess). GST and Currency are in the
 * sample file but the import does not read them, so the guide says so instead
 * of describing a value nobody stores.
 */
export const ITEM_CSV_HELP_COLUMNS: ReadonlyArray<Readonly<ItemCsvHelpColumn>> = [
  { labelKey: 'Item Name', helpKey: 'ITEM_NAME', required: true },
  { labelKey: 'Item Code', helpKey: 'ITEM_CODE' },
  { labelKey: 'Unit Quantity', helpKey: 'UNIT_QUANTITY' },
  { labelKey: 'Units', helpKey: 'UNITS' },
  { labelKey: 'Deactivated', helpKey: 'DEACTIVATED' },
  { labelKey: 'Category', helpKey: 'CATEGORY', required: true },
  { labelKey: 'Subcategory', helpKey: 'SUBCATEGORY' },
  { labelKey: 'Images', helpKey: 'IMAGES' },
  { labelKey: 'Item Discount', helpKey: 'ITEM_DISCOUNT' },
  { labelKey: 'GST', helpKey: 'GST' },
  { labelKey: 'Fix Cost Per Unit', helpKey: 'FIX_COST_PER_UNIT' },
  { labelKey: 'Sales Commission', helpKey: 'SALES_COMMISSION' },
  { labelKey: 'Minimum Price', helpKey: 'MINIMUM_PRICE' },
  { labelKey: 'Price A', helpKey: 'PRICE_A', required: true },
  { labelKey: 'Tax Name', helpKey: 'TAX_NAME' },
  { labelKey: 'Type', helpKey: 'TYPE' },
  { labelKey: 'Currency', helpKey: 'CURRENCY' },
  { labelKey: 'ITEM_CSV_HELP.CUSTOM_FIELDS_LABEL', helpKey: 'CUSTOM_FIELDS' },
];

/** Card #601: the collapsible "What goes in each column" guide, shown under the item sample CSV link only. */
@Component({
  selector: 'app-item-csv-help',
  standalone: true,
  imports: [TranslateModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <details class="item-csv-help mt-2 text-start">
      <summary class="sampletxt">{{ 'COMMON.ITEM_CSV_HELP.TITLE' | translate }}</summary>
      <p class="item-csv-help__intro mt-2 mb-2">{{ 'COMMON.ITEM_CSV_HELP.INTRO' | translate }}</p>
      <dl class="item-csv-help__list mb-0">
        @for (column of columns; track column.helpKey) {
          <dt class="item-csv-help__column">
            {{ 'COMMON.' + column.labelKey | translate }}
            @if (column.required) {
              <span class="item-csv-help__required">({{ 'COMMON.ITEM_CSV_HELP.REQUIRED' | translate }})</span>
            }
          </dt>
          <dd class="item-csv-help__text">{{ 'COMMON.ITEM_CSV_HELP.' + column.helpKey | translate }}</dd>
        }
      </dl>
    </details>
  `,
  styles: [
    `
      .item-csv-help {
        max-width: 420px;
        font-size: 13px;
      }
      .item-csv-help summary {
        cursor: pointer;
      }
      .item-csv-help__column {
        font-weight: 600;
        margin-top: 6px;
      }
      .item-csv-help__required {
        font-weight: 400;
      }
      .item-csv-help__text {
        margin: 0;
      }
    `,
  ],
})
export class ItemCsvHelpComponent {
  readonly columns = ITEM_CSV_HELP_COLUMNS;
}
